/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin/organisations/programs/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import { useBoolean, useTranslation, useAuthorization } from 'hooks';

import { OrganisationProgramFields, StudyFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import { getLabels } from 'utils/convertor';
import NotificationService from 'utils/notification';
import AddProductDialog from 'views/components/admin/add-organisation-product-dialog';
import EditIndication from 'views/components/admin/organisations/edit-indication';
import AssignIndications from 'views/components/admin/organisations/programs/assign-indications';
import AssignProducts from 'views/components/admin/organisations/programs/assign-products';
import EditProgram from 'views/components/admin/organisations/programs/edit-program';
import ProgramsGrid from 'views/components/admin/organisations/programs/programs-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import 'views/App.scss';

const AdminOrganisationPrograms = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = user?.canWriteSystemAdminLevel();

    const [loadPrograms, { data, error, loading }] = useLazyQuery(GET_ORGANISATION_PROGRAM_PRODUCT);
    const showAddProgram = useBoolean(false);
    const showAssignIndications = useBoolean(false);
    const showAssignProducts = useBoolean(false);
    const showAddProductDialog = useBoolean(false);
    const showEditIndication = useBoolean(false);

    const [currentProgram, setCurrentProgram] = useState(null);

    useEffect(() => {
        loadPrograms();
    }, [user]);

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const programs =
        data?.organisation.programs.map((item: OrganisationProgramFields) => {
            let studiesAmount = 0;
            data?.organisation.studies.forEach((el: StudyFields) => {
                if (el.programsIds.indexOf(item.id) >= 0) studiesAmount += 1;
            });
            return {
                ...item,
                productsLabel: getLabels(item.productIds, data?.organisation.products, 'name'),
                indicationsLabel: getLabels(item.indicationIds, data?.organisation.indications, 'name'),
                studiesAmount,
            };
        }) ?? [];

    return (
        <>
            <EditIndication
                open={showEditIndication.value}
                indications={data?.organisation.indications}
                currentProgram={currentProgram}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add' && currentProgram)
                            NotificationService.showNotification(
                                t('admin:indicationSuccessfulCreatedAndAssigned', {
                                    id: currentProgram?.recordId ?? currentProgram?.id,
                                }),
                            );
                        setCurrentProgram(null);
                        showEditIndication.setFalse();
                    }
                }}
            />
            <AddProductDialog
                organisation={data?.organisation}
                open={showAddProductDialog.value}
                currentProgram={currentProgram}
                close={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add' && currentProgram)
                            NotificationService.showNotification(
                                t('admin:productSuccessfulCreateAndAssigned', {
                                    id: currentProgram?.recordId ?? currentProgram?.id,
                                }),
                            );
                        setCurrentProgram(null);
                        showAddProductDialog.setFalse();
                    }
                }}
            />
            <AssignProducts
                currentProgram={currentProgram}
                open={showAssignProducts.value}
                products={data?.organisation.products}
                onClose={(action?: string | any) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'open-add') {
                            showAssignProducts.setFalse();
                            showAddProductDialog.setTrue();
                            return;
                        }
                        if (action.success === true) {
                            NotificationService.showNotification(
                                t('admin:successfulAssignedProgramToProduct', {
                                    amount: action.amount,
                                    id: currentProgram?.recordId ?? currentProgram.id,
                                }),
                            );

                            if (!action.close) return;
                        }
                        setCurrentProgram(null);
                        showAssignProducts.setFalse();
                    }
                }}
            />
            <AssignIndications
                currentProgram={currentProgram}
                open={showAssignIndications.value}
                indications={data?.organisation.indications}
                onClose={(action?: string | any) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'open-add') {
                            showAssignIndications.setFalse();
                            showEditIndication.setTrue();
                            return;
                        }
                        if (action.success === true) {
                            NotificationService.showNotification(
                                t('admin:successfulAssignedIndicationToProduct', {
                                    amount: action.amount,
                                    id: currentProgram?.recordId ?? currentProgram.id,
                                }),
                            );

                            if (!action.close) return;
                        }
                        setCurrentProgram(null);
                        showAssignIndications.setFalse();
                    }
                }}
            />
            <EditProgram
                program={currentProgram}
                programs={programs}
                open={showAddProgram.value}
                onClose={(action?: string) => {
                    if (action === 'error') {
                        NotificationService.showNotification(t('errorMessage'));
                    } else {
                        if (action === 'add') {
                            NotificationService.showNotification(t('admin:programSuccessfulCreated'));
                        } else if (action === 'edit') {
                            NotificationService.showNotification(
                                t('admin:programSuccessfulUpdated', {
                                    id: currentProgram?.recordId ?? currentProgram.id,
                                }),
                            );
                        }
                        setCurrentProgram(null);
                        showAddProgram.setFalse();
                    }
                }}
            />
            <PageTitle text={t('admin:managePrograms')} />
            <br />
            <Button
                text="admin:addProgram"
                onClick={() => {
                    showAddProgram.setTrue();
                }}
                className="main-button"
                disabled={!canEdit}
                style={{ marginLeft: 'auto', display: 'flex', marginTop: 15, marginBottom: 20 }}
            />
            <ProgramsGrid
                programs={programs}
                rowActions={(program: OrganisationProgramFields) => [
                    {
                        label: 'admin:editProgram',
                        icon: ['fal', 'edit'],
                        enabled: canEdit,
                        onClick: () => {
                            setCurrentProgram(program);
                            showAddProgram.setTrue();
                        },
                    },
                    {
                        label: 'admin:products',
                        icon: ['fal', 'link'],
                        onClick: () => {
                            setCurrentProgram(program);
                            showAssignProducts.setTrue();
                        },
                        enabled: canEdit,
                    },
                    {
                        label: 'admin:indications',
                        icon: ['fal', 'link'],
                        onClick: () => {
                            setCurrentProgram(program);
                            showAssignIndications.setTrue();
                        },
                        enabled: canEdit,
                    },
                ]}
            />
        </>
    );
};

export default AdminOrganisationPrograms;
